import Icon from '@atoms/Icon/Icon';
import { IIconPageLink } from '@models/IIconPageLink';
import classNames from 'classnames';
import {
  ButtonHTMLAttributes,
  DetailedHTMLProps,
  PropsWithChildren,
} from 'react';

interface WrapperProps {
  readonly url: string;
  readonly label?: string;
}

const Wrapper = ({
  children,
  label,
  url,
  ...buttonProps
}: PropsWithChildren<WrapperProps>) =>
  url ? (
    <a className="flex items-center gap-5" href={url} aria-label={label}>
      {children}
    </a>
  ) : (
    <button
      {...buttonProps}
      className="flex items-center gap-5"
      aria-label={label}
    >
      {children}
    </button>
  );

type IconPageLinkProps = IIconPageLink &
  DetailedHTMLProps<ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>;

const IconPageLink = ({
  active = false,
  icon,
  label,
  secondaryLabel,
  url,
  dataTestId = 'Icon-Page-Link',
  ...buttonProps
}: IconPageLinkProps) => {
  const circleClasses = classNames(
    {
      'text-black-100 bg-grey-100 hover:bg-grey active:bg-black-100': !active,
    },
    {
      'text-white bg-black-100 hover:bg-grey hover:text-black-100 active:bg-grey-200':
        active,
    },
    'w-13 h-13 rounded-full flex shrink-0 items-center group justify-center'
  );

  return (
    <Wrapper
      {...buttonProps}
      label={label}
      url={`${url}`}
      data-testid={dataTestId}
    >
      <div className={circleClasses} data-testid={`${dataTestId}-Icon`}>
        <Icon
          name={icon}
          colorClassName={
            active
              ? 'text-white group-active:text-black-100'
              : 'text-black-100 group-active:text-white'
          }
        />
      </div>
      {(label || secondaryLabel) && (
        <div className="flex flex-col">
          {label && (
            <div className="body-two" data-testid={`${dataTestId}-Label`}>
              {label}
            </div>
          )}
          {secondaryLabel && (
            <div
              className={classNames(
                active ? 'text-black-100' : 'text-grey-600',
                'body-four'
              )}
              data-testid={`${dataTestId}-Secondary-Label`}
            >
              {secondaryLabel}
            </div>
          )}
        </div>
      )}
    </Wrapper>
  );
};

export default IconPageLink;
